import React, { useEffect } from 'react';
import { useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import CategoryPage from './categoryPage';
import UserManagerPage from './userManagerPage';
import AppointmentSetupPage from './appointmentSetupPage';
import DeleteAccountPage from './deleteAccountPage';
// Notifications and LoadingMask are now provided by parent via context
import { appendAccountToUrl, getAccountFromUrl } from '../../utils/appointments/urlUtils';

const SettingsPage = () => {
  // Get loading and notification context from parent (Dashboard)
  const { NotificationComponent, isPanelLoading, setIsPanelLoading } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();

  // DO NOT use useEnsureAccount() here - it would update URL based on localStorage
  // which defeats the purpose of URL-driven account isolation
  // Each tab should maintain its own account number from the URL
  // useEnsureAccount();

  // Get account number from URL for URL-driven settings
  const accountFromUrl = getAccountFromUrl(location.search);
  // Get acctId from localStorage for use in child components
  const acctId = localStorage.getItem('acctId');

  // Determine which tab is active based on the current path
  const getActiveTab = () => {
    const path = location.pathname;
    // Only allow users and delete tabs
    if (path.endsWith('/settings/delete') || path.endsWith('/appointments/settings/delete')) return 'delete';
    if (path.endsWith('/settings/users') || path.endsWith('/appointments/settings/users')) return 'users';
    return 'users'; // Default tab is now "Manage Users"
  };

  const activeTab = getActiveTab();

  // Handle tab navigation - preserve account from URL
  const handleTabChange = (tab) => {
    // Always use the current account from URL for navigation
    const currentAccount = getAccountFromUrl(location.search);
    switch (tab) {
      case 'users':
        navigate(appendAccountToUrl('/appointments/settings/users', currentAccount));
        break;
      case 'delete':
        navigate(appendAccountToUrl('/appointments/settings/delete', currentAccount));
        break;
      default:
        navigate(appendAccountToUrl('/appointments/settings/users', currentAccount));
        break;
    }
  };
  const renderContent = () => {
    // Pass account number and acctId to child components for URL-driven functionality
    const props = { accountFromUrl, acctId };
    switch (activeTab) {
      case 'users':
        return <UserManagerPage {...props} />;
      case 'delete':
        return <DeleteAccountPage {...props} />;
      default:
        return <UserManagerPage {...props} />; // Default to user management
    }
  };

  // Redirect /appointments/settings to /appointments/settings/users for clarity
  useEffect(() => {
    const path = location.pathname;
    if (path.endsWith('/appointments/settings') || path.endsWith('/settings')) {
      const currentAccount = getAccountFromUrl(location.search);
      navigate(appendAccountToUrl('/appointments/settings/users', currentAccount), { replace: true });
    }
    // eslint-disable-next-line
  }, []);
  return (
    <div className="container mx-auto px-4 py-6 relative">
      {/* NotificationComponent for displaying alerts */}
      {NotificationComponent && <NotificationComponent />}

      {/* LoadingMask for the panel, controlled by parent */}
      {/* Only show if isPanelLoading is true */}
      {/* The parent Dashboard should render LoadingMask overlay for the panel, so this is just a placeholder if needed */}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Settings</h1>
      </div>

      {/* Tab navigation (only Manage Users and Delete Account) */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          className={`py-2 px-6 font-medium text-sm border-b-2 ${activeTab === 'users'
            ? 'border-black text-black'
            : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          onClick={() => handleTabChange('users')}
        >
          Manage Users
        </button>
        <button
          className={`py-2 px-6 font-medium text-sm border-b-2 ${activeTab === 'delete'
            ? 'border-black text-black'
            : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          onClick={() => handleTabChange('delete')}
        >
          Delete Account
        </button>
      </div>

      {/* Render content based on the active tab */}
      {renderContent()}
    </div>
  );
};

export default SettingsPage;
